import { Component, computed, inject, input } from '@angular/core'
import { RouterLink } from '@angular/router'

import { Store } from '@ngrx/store'
import { TooltipModule } from 'primeng/tooltip'

import { FlxButtonComponent } from '@flowx/angular-ui-toolkit'
import {
  HeaderAction,
  HeaderObject,
} from '@flowx/designer/modules/analytics/analytics-header.entity'
import { selectAnalyticsArea } from '@flowx/designer/modules/analytics/analytics-header.selectors'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { FX_ORGANIZATION_CODE_TOKEN } from '@flowx/designer/modules/auth/auth.constants'
import { TOOLTIP_DEFAULT_DELAY } from '@flowx/designer/modules/core/dictionary/constants'
import { ReturnTargetService } from '@flowx/designer/modules/layout/services/return-target.service'
import { LastUsedWorkspaceService } from '@flowx/designer/modules/workspaces/services/last-used-workspace.service'
import { landingWorkspaceId } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import type { RouteArea } from '@flowx/designer/shared/store/selectors/router.selectors'

/** Where landing sends an Org Owner, before any workspace. */
const SOLUTIONS_LINK = ['/guard', 'organisation-admin', 'projects']

/**
 * The left of the global header: the logo, which leads home, and in Organization Settings Go back
 * ("Back to" the workspace) and the organization's name.
 */
@Component({
  selector: 'flx-header-home',
  imports: [RouterLink, TooltipModule, FlxButtonComponent],
  templateUrl: './header-home.component.html',
  styleUrl: './header-home.component.scss',
  host: { class: 'flex items-center gap-x-3' },
})
export class FlxHeaderHomeComponent {
  #returnTargetService = inject(ReturnTargetService)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #lastUsedWorkspaceService = inject(LastUsedWorkspaceService)
  #analyticsService = inject(AnalyticsService)
  #analyticsArea = inject(Store).selectSignal(selectAnalyticsArea)

  /**
   * The workspace landing would lead to. `null` while last-used is read, since it could still
   * change, and for a user without workspaces.
   */
  #landingWorkspaceId = computed((): string | null =>
    this.#lastUsedWorkspaceService.loading()
      ? null
      : landingWorkspaceId(
          this.#workspacesAndPermissionsStore.availableWorkspacesForUser(),
          this.#lastUsedWorkspaceService.workspaceId()
        )
  )

  /** With no workspace in the URL, an Org Owner's logo leads to Solutions, as landing does. */
  #leadsToSolutions = computed(
    () =>
      this.area() === 'none' && this.#workspacesAndPermissionsStore.userIsOrganizationSaasAdmin()
  )

  protected TOOLTIP_DEFAULT_DELAY = TOOLTIP_DEFAULT_DELAY

  organizationCode = inject(FX_ORGANIZATION_CODE_TOKEN)

  area = input.required<RouteArea>()

  selectedWorkspace = this.#workspacesAndPermissionsStore.selectedWorkspace
  availableWorkspaces = this.#workspacesAndPermissionsStore.availableWorkspacesForUser
  /** Set only while there is a screen to go back to. */
  goBackDestination = this.#returnTargetService.goBackDestination
  /** The workspace Go back returns to, named on the button; Go back shows with it. */
  goBackWorkspaceName = computed((): string | null => {
    const workspaceId = this.goBackDestination()?.workspaceId
    return this.availableWorkspaces().find(({ id }) => id === workspaceId)?.name ?? null
  })

  /** The workspace whose Projects page the logo leads to; `null` when it leads to none. */
  homeWorkspaceId = computed((): string | null => {
    switch (this.area()) {
      case 'workspace':
        return this.selectedWorkspace()?.id ?? null
      // Organization Settings is not a workspace: the logo leads to the last-used workspace, or
      // the default one, and only while Go back is shown.
      case 'org':
        return this.goBackDestination() ? this.#landingWorkspaceId() : null
      // No workspace in the URL, as on the access-denied page: the logo leads where landing would,
      // which for an Org Owner is Solutions. A user without workspaces, on the "not assigned"
      // page, gets no link.
      case 'none':
        return this.#leadsToSolutions() ? null : this.#landingWorkspaceId()
    }
  })

  /** `null` leaves the logo unclickable. */
  homeLink = computed(() => {
    if (this.#leadsToSolutions()) {
      return SOLUTIONS_LINK
    }

    const workspaceId = this.homeWorkspaceId()
    return workspaceId ? ['/guard', 'workspace', workspaceId] : null
  })

  onLogoClick(): void {
    if (!this.homeLink()) {
      return
    }

    this.#analyticsService.trackHeader({
      event: { object: HeaderObject.LOGO, action: HeaderAction.CLICKED },
      properties: { area: this.#analyticsArea() },
    })

    this.#saveHomeWorkspace()
  }

  goBack(): void {
    const destination = this.goBackDestination()

    if (destination) {
      this.#analyticsService.trackHeader({
        event: { object: HeaderObject.ORGANIZATION_SETTINGS, action: HeaderAction.EXITED },
        properties: {
          landedOn: destination.fellBackToDefaultWorkspace
            ? 'default_workspace'
            : 'previous_screen',
        },
      })
    }

    this.#returnTargetService.goBack()
  }

  /**
   * The logo is a pick: in a workspace it saves the one the user is in, which they may have reached
   * by a link. Anywhere else it leads where landing would, so it saves the same way.
   */
  #saveHomeWorkspace(): void {
    const workspaceId = this.homeWorkspaceId()

    if (!workspaceId) {
      return
    }

    if (this.area() === 'workspace') {
      this.#lastUsedWorkspaceService.save(workspaceId)
    } else {
      this.#lastUsedWorkspaceService.saveLandingPick(workspaceId)
    }
  }
}
